import axios from "axios";

// =====================================================================
// Fortiqa frontend API client.
//
// Cloudflare Pages: REACT_APP_BACKEND_URL is unset → all API calls
// use relative paths (/api/*) hitting Pages Functions on the same
// origin (no CORS).
//
// Legacy FastAPI deployment: REACT_APP_BACKEND_URL points to the
// external backend host → axios prepends it.
//
// Two callers are supported:
//   • axios instance (default export `api`) — used by existing 40+
//     frontend pages.   Example: `api.get("/auth/me")`
//   • fetch helper `apiRequest()` — for new code that prefers a thin
//     wrapper around fetch().   Example: `apiRequest("/api/health")`
// =====================================================================

const RAW_BASE = (process.env.REACT_APP_BACKEND_URL || "").trim();
export const BACKEND_URL = RAW_BASE;
export const API = RAW_BASE ? `${RAW_BASE.replace(/\/$/, "")}/api` : "/api";

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach token from sessionStorage as fallback (in case cookie path fails on cross-origin)
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("nyk_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Lightweight fetch wrapper for relative API paths.
 *
 *   await apiRequest("/api/health")
 *   await apiRequest("/api/claims", { method: "POST", body: JSON.stringify({...}) })
 *
 * On Cloudflare Pages this stays same-origin. On the FastAPI deployment
 * the absolute BACKEND_URL is prepended if the path starts with `/api`.
 */
export async function apiRequest(path, options = {}) {
  const url =
    RAW_BASE && path.startsWith("/api")
      ? `${RAW_BASE.replace(/\/$/, "")}${path}`
      : path;
  const token = sessionStorage.getItem("nyk_token");
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      data.error || data.detail || data.message || `API ${response.status}`,
    );
  }
  return data;
}

export default api;
